import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { ContactMaster, ContactDescription, ContactTag } from "../types";
import {
    getContactDescription,
    saveContact,
    getContactDescriptionListener,
    getSharedContactDescriptionListener,
    createEmptyPersonalFact,
    getContactTagsListener,
    createEmptyContact,
    savePersonalFact,
    deletePersonalFact,
} from "../firebase";
import FactCard, {
    Container as FactContainer,
    CenterEditRow,
} from "./fact-card";
import {
    CircularProgress,
    TextField,
    Menu,
    MenuItem,
    Button,
    Autocomplete,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Dropdown from "./dropdown";
import Image from "./image";
import { CONTACT_MASTER } from "../default-types";
import { getImageUrl } from "../utils";
import { PrimaryText, Separator } from "./common";
import DatePicker from "./date-picker";
import { ReactComponent as ShareIcon } from "../assets/share.svg";
import debounce from "debounce";
import EditTags from "./edit-tags";
import Confirm from "./confirm";
import { Dropdown as SemanticDropdown } from "semantic-ui-react";
import { RootState } from "../redux/root-store";
import { connect } from "react-redux";
import { copyTextToClipboard } from "../utils";

const Container = styled.div`
    padding-left: 2em;
    padding-bottom: 3em;
    height: 100%;
    display: block;
    flex-grow: 1;
    overflow: auto;
    background-color: white;
    & .close-row {
        display: none;
    }
    @media only screen and (max-width: 720px) {
        overflow: auto;
        max-width: 100%;
        min-width: 100%;
        padding: 0em 1em;
        width: 100%;
        z-index: 2;
        position: absolute;
        top: 0em;
        left: 0em;
        min-height: 100%;

        & .close-row {
            display: flex;
            justify-content: flex-end;
        }
        & .close-row:hover {
            cursor: pointer;
        }
    }
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    margin: 1em 0em;
    & > *:first-child {
        margin-left: auto;
    }
    & > *:last-child {
        margin-right: auto;
    }
`;

const HowYouMet = styled("span")(
    ({ theme, $isActive }: any) => `
                                 ${
                                     $isActive &&
                                     `color: ${theme.palette.primary.main};`
                                 };
`
);

const SimpleRow = styled.div`
    display: flex;
    align-items: center;
    margin: 0em;
`;

const ContactHeader = styled.div`
    background-color: white;
    position: sticky;
    top: 0em;
    width: 100%;
    z-index: 3;
    display: flex;
    flex-direction: column;
    margin-bottom: 1em;
`;

const NoFactsActionButton = styled(Button)`
    width: 100%;
    margin-bottom: 1rem;
`;

const Tag = styled.span`
    padding: 0em 0.5em;
    border: 1px solid black;
    margin: 0 0.1em;
    border-radius: 5px;
    line-height: 1.2em;
    white-space: nowrap;
`;

const EditNameTextfield = styled(TextField)`
    padding: 0;
    font-size: 1.25rem;
    font-weight: 500;
    text-align: center;

    & input::placeholder {
        padding: 0;
        font-size: 1.25rem;
        font-weight: 500;
        text-align: center;
    }
`;

const mapStateToProps = (state: RootState) => ({
    loggedUser: state.session.user,
});

interface Props extends ReturnType<typeof mapStateToProps> {
    isEditingContact?: boolean;
    containerProps?: React.CSSProperties;
    setContact?: (contact: ContactMaster | null) => void;
    setIsEditingContact?: (isEditingContact: boolean) => void;
    onDeleteContact?: (contact: ContactMaster) => void;
    searchByTag?: (tag: ContactTag) => void;
    searchByHowYouMet?: (howYouMet: string, howYouMetId: string) => void;
    howYouMetLookUp?: (
        concept: string
    ) => { howYouMet: string; howYouMetId: string }[];
    goToContact?: (contactId: string) => void;
    allTags: ContactTag[];
}
const factSorting = (a: ContactDescription, b: ContactDescription) => {
    if (!a.created_date && !b.created_date) return 0;
    if (!a.created_date) return 1;
    if (!b.created_date) return -1;
    if (a.created_date === b.created_date) return 0;
    if (a.created_date > b.created_date) return -1;
    return 1;
};

const MySharedProfile = (props: Props) => {
    const { isEditingContact, setContact } = props;
    const [tags, setTags] = useState<ContactTag[]>([]);
    const [newDescriptions, setNewDescriptions] = useState<
        ContactDescription[]
    >([]);
    const [editContact, setEditContact] =
        useState<ContactMaster>(CONTACT_MASTER);
    const [isLoadingDescription, setIsLoadingDescription] =
        useState<boolean>(true);
    const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);
    const open = anchorEl ? true : false;
    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const [isChangingDaysSinceLastContact, setIsChangingDaysSinceLastContact] =
        useState<boolean>(false);
    const [daysSinceLastContactDate, setDaysSinceLastContactDate] =
        useState<string>("");
    const [isEditingHowYouMet, setIsEditingHowYouMet] =
        useState<boolean>(false);
    const [howYouMetOptions, setHowYouMetOptions] = useState<
        { howYouMet: string; howYouMetId: string }[]
    >([]);
    const handleClose = () => {
        setAnchorEl(null);
    };
    const [isEditingGroups, setIsEditingGroups] = useState<boolean>(false);
    const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
    const [descriptions, setDescriptions] = useState<ContactDescription[]>([]);

    const handleSaveContact = async () => {
        await saveContact(editContact);
        setContact && setContact(editContact);
        props.setIsEditingContact && props.setIsEditingContact(false);

        setEditContact(CONTACT_MASTER);
    };

    const handleAddNewFact = async (factKind: number) => {
        const newFact = await createEmptyPersonalFact();
        newFact.factKind = factKind;
        if (factKind === 3) {
            newFact.remindOnDate = "12#Months";
        }
        setNewDescriptions((descriptions) => [...descriptions, newFact]);
    };

    useEffect(() => {
        const unsubscribeDescriptions = getSharedContactDescriptionListener(
            (props.loggedUser && props.loggedUser.user_id) || "",
            (facts: ContactDescription[]) => {
                setDescriptions(facts.sort(factSorting));
                setIsLoadingDescription(false);
            }
        );
        return () => {
            unsubscribeDescriptions();
        };
    }, []);

    const handleCopyLink = () => {
        if (!props.loggedUser) return;
        copyTextToClipboard(
            `${window.location.origin}/share?contactId=${props.loggedUser.user_id}`
        );
    };

    if (!props.loggedUser) return null;

    return (
        <Container
            style={{
                ...props.containerProps,
            }}
        >
            <div>
                <ContactHeader>
                    <Row style={{ margin: "0" }}>
                        <div />
                        <>
                            <label
                                htmlFor="upload-contact-profile-picture"
                                style={{ position: "relative" }}
                            >
                                <Image
                                    src={getImageUrl(props.loggedUser.profile)}
                                    style={{
                                        maxWidth: "3.5em",
                                        maxHeight: "3.5em",
                                        borderRadius: "50%",
                                        marginRight: "1em",
                                        cursor: "pointer",
                                        width: "100%",
                                        minHeight: "100%",
                                        objectFit: "cover",
                                    }}
                                />
                                {isEditingContact && (
                                    <Image
                                        src="/static/ic_editProfile.png"
                                        style={{
                                            width: "1em",
                                            height: "1em",
                                            position: "absolute",
                                            bottom: "0px",
                                            right: "1em",
                                            cursor: "pointer",
                                        }}
                                    />
                                )}
                            </label>
                            <input
                                disabled={true}
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                id="upload-contact-profile-picture"
                                onChange={(
                                    evt: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                    if (!evt.target.files) return;
                                    // saveContact(contact, evt.target.files[0]);
                                }}
                            />
                        </>
                        <span
                            style={{
                                fontSize: "1.25rem",
                                lineHeight: "1.25rem",
                                fontWeight: 500,
                                textAlign: "center",
                            }}
                        >
                            {props.loggedUser.name}
                        </span>
                        <div
                            style={{
                                margin: "0em 0.5rem 0em 0.5rem",
                                textAlign: "center",
                                lineHeight: "1em",
                            }}
                        >
                            {descriptions.length} <br /> Notes
                        </div>
                        <ShareIcon
                            style={{ marginRight: "auto", cursor: "pointer" }}
                            onClick={handleCopyLink}
                        />
                    </Row>
                    <Separator
                        style={{ position: "absolute", bottom: "0px" }}
                    />
                </ContactHeader>

                <div style={{ overflow: "auto" }}>
                    {descriptions.length === 0 && isLoadingDescription && (
                        <FactContainer
                            style={{
                                justifyContent: "center",
                                display: "flex",
                                border: "0",
                            }}
                        >
                            <CircularProgress />
                        </FactContainer>
                    )}
                    {newDescriptions.length === 0 &&
                        descriptions.length === 0 &&
                        !isLoadingDescription && (
                            <>
                                <NoFactsActionButton
                                    variant="contained"
                                    style={{ width: "100%" }}
                                    onClick={() => {
                                        handleAddNewFact(0);
                                    }}
                                >
                                    Add A Note
                                </NoFactsActionButton>
                                <NoFactsActionButton
                                    variant="contained"
                                    style={{ width: "100%" }}
                                    onClick={() => {
                                        handleAddNewFact(3);
                                    }}
                                >
                                    Add A Birthday
                                </NoFactsActionButton>
                                <NoFactsActionButton
                                    variant="contained"
                                    style={{ width: "100%" }}
                                    onClick={() => {
                                        handleAddNewFact(2);
                                    }}
                                >
                                    Add A Child
                                </NoFactsActionButton>
                                <NoFactsActionButton
                                    variant="contained"
                                    style={{ width: "100%" }}
                                    onClick={() => {
                                        handleAddNewFact(1);
                                    }}
                                >
                                    Add A Location
                                </NoFactsActionButton>
                            </>
                        )}

                    {descriptions.length > 0 &&
                        descriptions.map((description: ContactDescription) => (
                            <FactCard
                                hideReminder={true}
                                deleteFact={deletePersonalFact}
                                saveContactDescription={savePersonalFact}
                                key={description.desc_id}
                                fact={{ ...description, sharedFact: false }}
                            />
                        ))}

                    {newDescriptions.map((description: ContactDescription) => (
                        <FactCard
                            hideReminder={true}
                            saveContactDescription={savePersonalFact}
                            deleteFact={deletePersonalFact}
                            newFact={true}
                            key={description.desc_id}
                            fact={description}
                            contactId={""}
                            discardNewFact={(desc_id: string) => {
                                setNewDescriptions((newDescriptions) =>
                                    newDescriptions.filter(
                                        (description: ContactDescription) =>
                                            description.desc_id !== desc_id
                                    )
                                );
                            }}
                        />
                    ))}
                </div>
            </div>
            <div
                style={{
                    position: "fixed",
                    bottom: "1em",
                    right: "1em",
                    display: "flex",
                    justifyContent: "end",
                    paddingRight: "1em",
                }}
            >
                <Dropdown
                    options={
                        <>
                            <MenuItem onClick={() => handleAddNewFact(0)}>
                                Add a note
                            </MenuItem>
                            <MenuItem onClick={() => handleAddNewFact(2)}>
                                Add a child
                            </MenuItem>
                            <MenuItem onClick={() => handleAddNewFact(1)}>
                                Add a location
                            </MenuItem>
                            <MenuItem onClick={() => handleAddNewFact(3)}>
                                Add a birthday
                            </MenuItem>
                        </>
                    }
                >
                    <Button
                        style={{
                            borderRadius: "50%",
                            fontWeight: "500",
                            minWidth: "1.5em",
                            minHeight: "1.5em",
                            width: "1.5em",
                            height: "1.5em",
                            fontSize: "2em",
                            alignItems: "unset",
                            lineHeight: "1.1em",
                        }}
                        variant="contained"
                    >
                        +
                    </Button>
                </Dropdown>
            </div>
        </Container>
    );
};

export default connect(mapStateToProps, {})(MySharedProfile);
